import { useAutoneTranslation } from '@autone/translations';
import {
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  useMemo,
} from 'react';

import {
  Button,
  type ButtonProps,
  Drawer,
  DrawerContent,
} from '../../../atoms';
import { cn } from '../../../utils';
import {
  type AdvancedFiltersConfig,
  type InferAdvancedFilterPublicState,
  useAdvancedFiltersContext,
} from '../advanced-filters';
import { advancedFilterConditionIsComplete } from '../advanced-filters/utilities/reducer-utils';
import { useGeneralFiltersContext } from '../general-filters';

import {
  EditSavedFilterView,
  NoSavedFilterTemplateData,
  SavedFiltersList,
  SavedFiltersSectionHeader,
} from './SavedFilters.components';
import {
  SavedFiltersActionsContextProvider,
  SavedFiltersSelectionContextProvider,
  useSavedFiltersActionsContext,
  useViewedSavedFilterContext,
} from './SavedFilters.context';
import {
  type SavedFiltersActionsContextValue,
  type ViewedSavedFilterContextValue,
} from './types';

interface SavedFiltersContextProviderProps<
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
> extends SavedFiltersActionsContextValue<Config>,
    ViewedSavedFilterContextValue,
    PropsWithChildren {}

export const SavedFiltersContextProvider = <
  Config extends AdvancedFiltersConfig,
>({
  savedFilterTemplates,
  viewedSavedFilterId,
  setViewedSavedFilterId,
  editingSavedFilterId,
  setEditingSavedFilterId,
  onApplySavedFilter,
  onDeleteSavedFilter,
  onEditSavedFilterName,
  onEditSavedFilter,
  onTogglePinSavedFilter,
  children,
}: SavedFiltersContextProviderProps<Config>) => {
  return (
    <SavedFiltersActionsContextProvider<Config>
      savedFilterTemplates={savedFilterTemplates}
      onApplySavedFilter={onApplySavedFilter}
      onDeleteSavedFilter={onDeleteSavedFilter}
      onEditSavedFilterName={onEditSavedFilterName}
      onEditSavedFilter={onEditSavedFilter}
      onTogglePinSavedFilter={onTogglePinSavedFilter}
    >
      <SavedFiltersSelectionContextProvider
        viewedSavedFilterId={viewedSavedFilterId}
        setViewedSavedFilterId={setViewedSavedFilterId}
        editingSavedFilterId={editingSavedFilterId}
        setEditingSavedFilterId={setEditingSavedFilterId}
      >
        {children}
      </SavedFiltersSelectionContextProvider>
    </SavedFiltersActionsContextProvider>
  );
};

type SavedFiltersContentProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'children'
>;

export const SavedFiltersContent = forwardRef<
  HTMLDivElement,
  SavedFiltersContentProps
>(({ className, ...props }, ref) => {
  const { common } = useAutoneTranslation();
  const { savedFilterTemplates } = useSavedFiltersActionsContext();

  return (
    <>
      <div {...props} ref={ref} className={cn('flex flex-col', className)}>
        {savedFilterTemplates.length === 0 && (
          <div className="flex flex-col gap-4">
            <SavedFiltersSectionHeader title={common('filters.saved')} />
            <NoSavedFilterTemplateData />
          </div>
        )}
        <SavedFiltersList />
      </div>
    </>
  );
});

export const EditSavedFilterDrawer = () => {
  const { savedFilterTemplates } = useSavedFiltersActionsContext();
  const { editingSavedFilterId, setEditingSavedFilterId } =
    useViewedSavedFilterContext();
  const { filterGroups: generalFiltersConfig, toggledOptions } =
    useGeneralFiltersContext();
  const { state: advancedFiltersState, config: advancedFiltersConfig } =
    useAdvancedFiltersContext();

  const currentAdvancedFilters = useMemo(() => {
    const completedConditions = Object.entries(advancedFiltersState.conditions)
      .filter(([, condition]) => advancedFilterConditionIsComplete(condition))
      .map(([rowId, condition]) => ({
        ...condition,
        id: rowId,
      })) as InferAdvancedFilterPublicState<AdvancedFiltersConfig>['conditions'];

    if (!completedConditions.length) {
      return null;
    }

    return {
      conditionCombineMode: advancedFiltersState.conditionCombineMode,
      conditions: completedConditions,
    };
  }, [advancedFiltersState]);

  const editingTemplate = savedFilterTemplates.find(
    (template) => template.id === editingSavedFilterId,
  );

  return (
    <Drawer
      open={!!editingSavedFilterId}
      onOpenChange={(open) => !open && setEditingSavedFilterId(null)}
    >
      <DrawerContent className="w-[768px] max-w-[90vw]">
        {editingTemplate && (
          <EditSavedFilterView
            savedFilterTemplate={editingTemplate}
            generalFiltersConfig={generalFiltersConfig}
            advancedFiltersConfig={advancedFiltersConfig}
          />
        )}
        {!editingTemplate && editingSavedFilterId && (
          <EditSavedFilterView
            savedFilterTemplate={{
              name: '',
              id: editingSavedFilterId,
              selectedGeneralFilters: {},
              advancedFilters: null,
              isPinned: false,
            }}
            generalFiltersConfig={generalFiltersConfig}
            advancedFiltersConfig={advancedFiltersConfig}
            initialSelectedGeneralFilters={toggledOptions}
            initialAdvancedFilters={currentAdvancedFilters}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
};

export type SavedFilterApplyButtonProps = ButtonProps;

export const SavedFilterApplyButton = forwardRef<
  HTMLButtonElement,
  SavedFilterApplyButtonProps
>(({ className, onClick, ...props }, ref) => {
  const { viewedSavedFilterId } = useViewedSavedFilterContext();
  const { onApplySavedFilter } = useSavedFiltersActionsContext();

  const handleOnClickApplyFilter = (e: MouseEvent<HTMLButtonElement>) => {
    if (viewedSavedFilterId) {
      onApplySavedFilter(viewedSavedFilterId);
    }
    onClick?.(e);
  };

  return (
    <Button
      onClick={handleOnClickApplyFilter}
      className={className}
      disabled={!viewedSavedFilterId}
      size={'sm'}
      {...props}
      ref={ref}
    />
  );
});
